import { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import { useSelector } from 'react-redux';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import NFT from '../../../components/NFT';
import { useSnackbar } from 'notistack';
import './style.css';
import { getAllTokens, getWalletTokens } from '../../../util/metamask';
import MetaMaskIcon from '../../../components/MetaMaskIcon';
import { MarketIcon, HistoryIcon, OfferIcon } from '../../../util/icons';
import Billboard from '../../../components/Billboard';
import MakeAnOffer from '../../../components/MakeAnOffer';
import { PATH_HISTORY } from '../../config';
import Page from '../../../components/Page';
import PageTitle from '../../../components/PageTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const Marketplace = () => {

    const isOwned = id => owned?.includes( id );

    const { enqueueSnackbar } = useSnackbar(),
    labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale ),
    accounts = useSelector( state => state.eth.accounts ),
    [ NFTs, setNFTs ] = useState( null ),
    [ owned, setOwned ] = useState( [] ),
    [ showOwned, setShowOwned ] = useState( true ),
    [ metadata, setMetadata ] = useState( null ),
    open = Boolean( metadata ),
    menu = [
        {
            icon: <OfferIcon />,
            hide: isOwned,
            label: labels.makeOffer,
            onClick: ( id, data) => {
                if( !accounts[ 0 ] ){
                    enqueueSnackbar(
                        labels.youMustBeConnected,
                        { variant: 'error' }
                    );
                } else {
                    setMetadata({ ...data, id });
                }
            }
        },
        {
            icon: <HistoryIcon />,
            label: labels.tokenHistory,
            to: PATH_HISTORY
        }
    ];

    /**
     * @name getNFTs
     * @function
     * @description Gets user's list of NFTs
     */
    const getNFTs = useCallback( async () => {
        const tokens = await getAllTokens();
        setNFTs( tokens );
    }, [ setNFTs ]);

    /**
     * @name getWalletNFTs
     * @function
     * @description Retrieves the user's wallet contents
     */
    const getWalletNFTs = useCallback( async () => {
        const tokens = await getWalletTokens();
        setOwned( tokens );
    }, [ setOwned ]);

    const toggleShow = () => {
        setShowOwned( !showOwned );
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // update connected status
    useEffect(() => {
        if( !NFTs ) getNFTs();
    }, [ NFTs, getNFTs, getWalletNFTs ]);

    // prevent request before wallet connects
    useEffect(() => {
        if( accounts[ 0 ] ) getWalletNFTs();
    }, [ accounts, getWalletNFTs ])
   
    return (
        <>
            <Page modifier="marketplace" title={ labels.title }>
                <FormControl>
                    <FormControlLabel 
                        control={
                            <Switch checked={ showOwned } /> 
                        }    
                        label={ labels.showOwned }
                        onChange={ toggleShow } />
                </FormControl>
                
                <PageTitle 
                    Icon={ MarketIcon } 
                    title={ labels.title }
                    subtitle={ labels.subtitle } />
                <Grid container justifyContent="center">
                    {
                        NFTs?.map(
                            token => {

                                if( !showOwned && isOwned( token ) ) return null;

                                return (
                                    <Grid item key={ token } md={ 4 } sm={ 6 } xs={ 12 }>
                                        <NFT id={ token } menu={ menu } owned={ isOwned( token ) } /> 
                                    </Grid>
                                )
                            }
                        )
                    }
                </Grid>
                {
                    // wallet present, account unlocked, no NFTs
                    window.ethereum && accounts.length && ( !NFTs || NFTs.length === 0 ) ?
                        <div className="coe__marketplace__noData">
                            <Billboard alignItems="center" body={ labels.noDataBody } Icon={ LinkOffIcon } title={ labels.noDataTitle } />
                        </div>:
                        null
                }
                {
                    // wallet present, account unlocked, no NFTs
                    window.ethereum && !accounts.length ?
                        <div className="coe__marketplace__noData">
                            <Billboard alignItems="center" body={ labels.notConnectedBody } Icon={ MetaMaskIcon } title={ labels.notConnectedTitle } />
                        </div>:
                        null
                }
                {
                    // no wallet, no account
                    !window.ethereum && !accounts.length ?
                        <div className="coe__marketplace__noData">
                            <Billboard alignItems="center" body={ labels.notInstalled } Icon={ MetaMaskIcon } title={ labels.connect } />
                        </div>:
                        null
                }
            </Page>
            <MakeAnOffer open={ open } metadata={ metadata } onClose={ () => setMetadata( null ) } />
        </>
    )

}

export default Marketplace;